import { History } from 'history'
import { useEffect, useRef } from 'react'

import {
  getPatientDetail,
  getProgramFromSearchIndexById
} from '../../../services/ApiService'
import { OrgAddressDto, ProgramCatalogDto } from '../../../types/swaggerTypes'
import {
  isPatientDetailPostTrialAccess,
  PTA_TRANSFER_RESTRICTED_MESSAGE
} from '../patientInitialsDisplay'

export { PTA_TRANSFER_RESTRICTED_MESSAGE }

export function isPtaPatientTransferBlocked(
  patientProgramStatus: string | null | undefined,
  programCatalog: ProgramCatalogDto | undefined,
  defaultShippingAddress?: OrgAddressDto | null,
  instituteCountryCode?: string | null
): boolean {
  return isPatientDetailPostTrialAccess(
    patientProgramStatus,
    programCatalog,
    defaultShippingAddress,
    instituteCountryCode
  )
}

export async function fetchIsPtaPatientTransferBlocked(
  patientId: string,
  physicianId: string,
  defaultShippingAddress?: OrgAddressDto | null,
  instituteCountryCode?: string | null
): Promise<boolean> {
  const patientResponse = await getPatientDetail(patientId, physicianId)
  const patient = patientResponse.data
  const programId = patient.program?.programId
  if (!programId) return true
  const catalogResponse = await getProgramFromSearchIndexById(
    programId.toString()
  )

  return isPtaPatientTransferBlocked(
    patient.program?.status,
    catalogResponse.data,
    defaultShippingAddress,
    instituteCountryCode
  )
}

export function redirectToPatientDetail(
  history: History,
  physicianId: string,
  patientId: string
): void {
  history.replace(`/programs/my-physicians/${physicianId}/${patientId}`)
}

/**
 * Redirects away from transfer routes when the patient belongs to a PTA program.
 */
export function useRedirectIfPtaTransferBlocked(
  patientId: string,
  physicianId: string,
  history: History,
  defaultShippingAddress?: OrgAddressDto | null,
  instituteCountryCode?: string | null
): void {
  const hasRedirectedRef = useRef(false)

  useEffect(() => {
    hasRedirectedRef.current = false
  }, [patientId, physicianId])

  useEffect(() => {
    if (!patientId || !physicianId || hasRedirectedRef.current) {
      return
    }

    let isCancelled = false

    fetchIsPtaPatientTransferBlocked(
      patientId,
      physicianId,
      defaultShippingAddress,
      instituteCountryCode
    )
      .then((isBlocked) => {
        if (isCancelled || !isBlocked || hasRedirectedRef.current) {
          return
        }

        hasRedirectedRef.current = true
        redirectToPatientDetail(history, physicianId, patientId)
      })
      .catch(() => {
        // Patient/catalog fetch errors are handled by the host container.
      })

    return () => {
      isCancelled = true
    }
  }, [
    patientId,
    physicianId,
    history,
    defaultShippingAddress,
    instituteCountryCode
  ])
}
